import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  getCreditUsage,
  getGroupCredits,
  getUserGroupInfo,
  addUserToGroup,
  listApiKeys,
  generateApiKey,
  revokeApiKey,
  createPaymentIntent,
  createSetupIntent,
  getAutoTopupSettings,
  disableAutoTopup,
  updateAutoTopup,
} from '../utils/api';
import './UserDashboard.css';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API_PUBLISHABLE_KEY);

// Setup Form Component for Auto Top-up
const SetupForm = ({ amount, threshold, billingEmail, onSuccess, onError, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [message, setMessage] = useState('');
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: billingEmail || localStorage.getItem('username') || '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !isReady) return;
    if (!billingDetails.name.trim()) {
      setMessage('Please enter your name');
      return;
    }

    setIsProcessing(true);
    setMessage('');

    try {
      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/setup-success`,
          payment_method_data: {
            billing_details: {
              name: billingDetails.name,
              email: billingDetails.email,
            },
          },
        },
      });

      if (error) {
        setMessage(error.message || 'An error occurred during setup');
        setIsProcessing(false);
        if (onError) onError(error);
      } else {
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      setMessage('An unexpected error occurred');
      setIsProcessing(false);
      if (onError) onError(err);
    }
  };

  return (
    <form onSubmit={handleSubmit} className='payment-form'>
      <div className='topup-summary'>
        <div className='summary-item'>
          <span className='summary-label'>Top-up Amount:</span>
          <span className='summary-value'>£{amount}</span>
        </div>
        <div className='summary-item'>
          <span className='summary-label'>Trigger When Balance Falls Below:</span>
          <span className='summary-value'>£{threshold}</span>
        </div>
        <div className='summary-item'>
          <span className='summary-label'>VAT Receipts To:</span>
          <span className='summary-value'>{billingEmail}</span>
        </div>
        <p className='summary-note'>
          Your card will be charged automatically when your balance is low.
        </p>
      </div>

      <div className='form-group'>
        <label className='label'>Cardholder Name</label>
        <input
          type='text'
          className='input'
          value={billingDetails.name}
          onChange={(e) => {
            setBillingDetails((prev) => ({ ...prev, name: e.target.value }));
            setMessage('');
          }}
          disabled={isProcessing}
          placeholder='John Smith'
          required
        />
      </div>

      <div className='form-group'>
        <label className='label'>Payment Method</label>
        <div className='stripe-element-wrapper'>
          <PaymentElement
            onReady={() => setIsReady(true)}
            onLoadError={(error) => {
              console.error('PaymentElement load error:', error);
              setMessage('Failed to load payment form. Please refresh the page.');
            }}
            options={{ layout: 'tabs' }}
          />
        </div>
      </div>

      {message && <div className='alert alert-error'>{message}</div>}

      <div className='button-group'>
        <button
          type='button'
          onClick={onBack}
          className='btn btn-secondary'
          disabled={isProcessing}
        >
          Back
        </button>
        <button
          type='submit'
          className='btn btn-primary btn-lg'
          disabled={!stripe || !elements || !isReady || isProcessing}
        >
          {isProcessing ? (
            <>
              <span className='spinner'></span>Setting up...
            </>
          ) : (
            'Enable Auto Top-up'
          )}
        </button>
      </div>

      {(!stripe || !elements || !isReady) && !isProcessing && (
        <p className='loading-hint'>
          {!stripe || !elements ? 'Loading payment system...' : 'Loading payment form...'}
        </p>
      )}
    </form>
  );
};

// Checkout Form Component for One-time Payment
const CheckoutForm = ({ amount, billingEmail, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [message, setMessage] = useState('');
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: billingEmail || localStorage.getItem('username') || '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !isReady) return;
    if (!billingDetails.name.trim()) {
      setMessage('Please enter your name');
      return;
    }

    const currentCredits = localStorage.getItem('groupCredits');
    if (currentCredits !== null) {
      localStorage.setItem('balanceBeforePayment', currentCredits);
    }

    setIsProcessing(true);
    setMessage('');

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success`,
          receipt_email: billingDetails.email,
          payment_method_data: {
            billing_details: {
              name: billingDetails.name,
              email: billingDetails.email,
            },
          },
        },
      });

      if (error) {
        setMessage(error.message || 'An error occurred during payment');
        setIsProcessing(false);
        if (onError) onError(error);
      } else {
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      setMessage('An unexpected error occurred');
      setIsProcessing(false);
      if (onError) onError(err);
    }
  };

  const handleInputChange = (field, value) => {
    setBillingDetails((prev) => ({ ...prev, [field]: value }));
    setMessage('');
  };

  return (
    <form onSubmit={handleSubmit} className='payment-form'>
      <div className='form-group'>
        <label className='label'>Full Name</label>
        <input
          type='text'
          className='input'
          value={billingDetails.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          disabled={isProcessing}
          placeholder='John Smith'
          required
        />
      </div>

      <div className='form-group'>
        <label className='label'>Payment Details</label>
        <div className='stripe-element-wrapper'>
          <PaymentElement
            onReady={() => setIsReady(true)}
            onLoadError={(error) => {
              console.error('PaymentElement load error:', error);
              setMessage('Failed to load payment form. Please refresh the page.');
            }}
            options={{ layout: 'tabs' }}
          />
        </div>
      </div>

      {message && <div className='alert alert-error'>{message}</div>}

      <button
        type='submit'
        className='btn btn-primary btn-lg btn-block'
        disabled={!stripe || !elements || !isReady || isProcessing}
      >
        {isProcessing ? (
          <>
            <span className='spinner'></span>Processing...
          </>
        ) : (
          `Pay £${amount}`
        )}
      </button>

      {(!stripe || !elements || !isReady) && !isProcessing && (
        <p className='loading-hint'>
          {!stripe || !elements ? 'Loading payment system...' : 'Loading payment form...'}
        </p>
      )}
    </form>
  );
};

const SPEND_PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

const UserDashboard = () => {
  const navigate = useNavigate();
  const username = localStorage.getItem('username');

  const [credits, setCredits] = useState(null);
  const [groupInfo, setGroupInfo] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const { isAdmin } = useAuth();

  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('DashboardTab') || 'overview';
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Forms
  const [newUser, setNewUser] = useState({ username: '', group_id: '' });
  const [newApiKeyName, setNewApiKeyName] = useState('');
  const [generatedKey, setGeneratedKey] = useState(null);

  // Payment States
  const [paymentStep, setPaymentStep] = useState('type');
  const [paymentType, setPaymentType] = useState(null);
  const [amount, setAmount] = useState('');
  const [threshold, setThreshold] = useState('10.00');
  const [billingEmail, setBillingEmail] = useState(username || '');
  const [clientSecret, setClientSecret] = useState('');
  const [setupSecret, setSetupSecret] = useState('');
  const [totalWithTax, setTotalWithTax] = useState(null);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  // Auto Top-up Management States
  const [autoTopupSettings, setAutoTopupSettings] = useState(null);
  const [isEditingTopup, setIsEditingTopup] = useState(false);
  const [editTopupAmount, setEditTopupAmount] = useState('');
  const [editTopupThreshold, setEditTopupThreshold] = useState('');

  const [spendPeriod, setSpendPeriod] = useState('30d');
  const [spendByPeriod, setSpendByPeriod] = useState({});
  const [spendLoading, setSpendLoading] = useState(false);
  const [spendError, setSpendError] = useState('');

  const currentSpendRows = spendByPeriod[spendPeriod];
  const spendRows = currentSpendRows || [];
  const sumUsage = (rows) => rows.reduce((sum, r) => sum + r.credits_consumed, 0);
  const spendTotalCredits = sumUsage(spendRows);
  const spendTotalTransactions = spendRows.reduce((sum, r) => sum + r.transaction_count, 0);
  const spend30dTotal = spendByPeriod['30d'] ? sumUsage(spendByPeriod['30d']) : null;

  const MINIMUM_AMOUNT = 50;
  const MAX_AMOUNT = 10000;

  const MINIMUM_THRESHOLD = 10;
  const MAX_THRESHOLD = 10000;

  const CREDITS_PER_POUND = 100;

  const poundsToCredits = (pounds) => Math.round(parseFloat(pounds) * CREDITS_PER_POUND);
  const formatCredits = (creditsInPence) => `£${(creditsInPence / 100).toFixed(2)}`;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    localStorage.setItem('DashboardTab', activeTab);
    if (activeTab !== 'purchase-credits') {
      resetPaymentFlow();
    }
  }, [activeTab]);

  useEffect(() => {
    let cancelled = false;
    const loadSpend = async () => {
      setSpendLoading(true);
      setSpendError('');
      try {
        const rows = await getCreditUsage(spendPeriod);
        setSpendByPeriod((prev) => ({ ...prev, [spendPeriod]: rows }));
      } catch (err) {
        if (!cancelled) setSpendError(err.message || 'Failed to load spend data');
      } finally {
        if (!cancelled) setSpendLoading(false);
      }
    };
    loadSpend();
    return () => {
      cancelled = true;
    };
  }, [spendPeriod]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [creditsRes, groupRes, keysRes] = await Promise.allSettled([
        getGroupCredits(username),
        getUserGroupInfo(username),
        listApiKeys(),
        loadAutoTopupSettings(), // already catches its own errors
      ]);

      if (creditsRes.status === 'fulfilled') setCredits(creditsRes.value);
      if (groupRes.status === 'fulfilled') setGroupInfo(groupRes.value.group);
      if (keysRes.status === 'fulfilled') setApiKeys(keysRes.value.api_keys || []);

      const failed = [creditsRes, groupRes, keysRes].find((r) => r.status === 'rejected');
      if (failed) setError(failed.reason?.message || 'Failed to load some dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadAutoTopupSettings = async () => {
    try {
      const data = await getAutoTopupSettings();
      setAutoTopupSettings(data.settings);
    } catch (err) {
      console.error('Failed to load auto top-up settings:', err);
      setAutoTopupSettings(null);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await addUserToGroup({ ...newUser });
      setSuccess('User invited successfully. They will receive an email to set their password.');
      setNewUser({ username: '', group_id: '' });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleGenerateApiKey = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setGeneratedKey(null);
    try {
      const data = await generateApiKey(newApiKeyName);
      setGeneratedKey(data);
      setNewApiKeyName('');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRevokeApiKey = async (name) => {
    if (!window.confirm(`Are you sure you want to revoke the API key "${name}"?`)) return;
    setError('');
    setSuccess('');
    try {
      await revokeApiKey(name);
      setSuccess('API key revoked successfully');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleTypeSelection = (type) => {
    setPaymentType(type);
    setPaymentStep('amount');
    setPaymentError('');
  };

  const handleAmountSubmit = async (e) => {
    e.preventDefault();
    setPaymentError('');

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setPaymentError('Please enter a valid amount');
      return;
    }
    if (amountNum < MINIMUM_AMOUNT) {
      setPaymentError(`Minimum purchase amount is £${MINIMUM_AMOUNT}`);
      return;
    }
    if (amountNum > MAX_AMOUNT) {
      setPaymentError(`Maximum purchase amount is £${MAX_AMOUNT}`);
      return;
    }
    console.log('Billing Email:', billingEmail);
    if (!billingEmail.trim()) {
      setPaymentError('Please enter a billing email');
      return;
    }

    if (paymentType === 'auto-topup') {
      const thresholdNum = parseFloat(threshold);
      if (isNaN(thresholdNum) || thresholdNum < MINIMUM_THRESHOLD) {
        setPaymentError(`Minimum threshold is £${MINIMUM_THRESHOLD}`);
        return;
      }
      if (thresholdNum > MAX_THRESHOLD) {
        setPaymentError(`Maximum threshold is £${MAX_THRESHOLD}`);
        return;
      }
    }

    setIsPaymentLoading(true);
    try {
      const amountInPence = Math.round(amountNum * 100);
      const thresholdInCredits = poundsToCredits(threshold);

      if (paymentType === 'one-time') {
        const data = await createPaymentIntent(billingEmail, amountInPence);
        if (!data.clientSecret) throw new Error('No client secret received from server');
        setClientSecret(data.clientSecret);
        setTotalWithTax(
          data.totalAmount ? (data.totalAmount / 100).toFixed(2) : amountNum.toFixed(2)
        );
        setPaymentStep('payment');
      } else {
        const data = await createSetupIntent(billingEmail, amountInPence, thresholdInCredits);
        if (!data.clientSecret) throw new Error('No setup secret received from server');
        setSetupSecret(data.clientSecret);
        setPaymentStep('setup');
      }
    } catch (err) {
      setPaymentError(err.message || 'Failed to initialize payment. Please try again.');
    } finally {
      setIsPaymentLoading(false);
    }
  };

  const resetPaymentFlow = () => {
    setPaymentStep('type');
    setPaymentType(null);
    setClientSecret('');
    setSetupSecret('');
    setTotalWithTax(null);
    setPaymentError('');
    setAmount('');
    setThreshold('10.00');
    setBillingEmail(username || '');
  };

  const handleBackToAmount = () => {
    setPaymentStep('amount');
    setClientSecret('');
    setSetupSecret('');
    setPaymentError('');
  };

  const handleDisableAutoTopup = async () => {
    if (!window.confirm('Are you sure you want to disable automatic top-ups?')) return;
    setError('');
    setSuccess('');
    setIsPaymentLoading(true);
    try {
      await disableAutoTopup();
      setSuccess('Automatic top-up has been disabled successfully');
      await loadAutoTopupSettings();
    } catch (err) {
      setError(err.message || 'Failed to disable auto top-up');
    } finally {
      setIsPaymentLoading(false);
    }
  };

  const handleEditTopup = () => {
    if (autoTopupSettings) {
      setEditTopupAmount((autoTopupSettings.topup_amount / 100).toFixed(2));
      setEditTopupThreshold((autoTopupSettings.threshold / 100).toFixed(2));
    }
    setIsEditingTopup(true);
  };

  const handleCancelEditTopup = () => {
    setIsEditingTopup(false);
    setEditTopupAmount('');
    setEditTopupThreshold('');
    setPaymentError('');
  };

  const handleUpdateAutoTopup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setPaymentError('');

    const amountNum = parseFloat(editTopupAmount);
    const thresholdNum = parseFloat(editTopupThreshold);

    if (isNaN(amountNum) || amountNum < MINIMUM_AMOUNT) {
      setPaymentError(`Minimum amount is £${MINIMUM_AMOUNT}`);
      return;
    }
    if (isNaN(thresholdNum) || thresholdNum < MINIMUM_THRESHOLD) {
      setPaymentError(`Minimum threshold is £${MINIMUM_THRESHOLD}`);
      return;
    }

    setIsPaymentLoading(true);
    try {
      await updateAutoTopup(Math.round(amountNum * 100), Math.round(thresholdNum * 100));
      setSuccess('Auto top-up settings updated successfully');
      setIsEditingTopup(false);
      setEditTopupAmount('');
      setEditTopupThreshold('');
      await loadAutoTopupSettings();
    } catch (err) {
      setPaymentError(err.message || 'Failed to update auto top-up');
    } finally {
      setIsPaymentLoading(false);
    }
  };

  return (
    <div className='user-dashboard'>
      <Navbar isAdmin={isAdmin} />

      <div className='dashboard-container'>
        <aside className='dashboard-sidebar'>
          <div className='sidebar-section'>
            <div className='balance-card'>
              <div className='balance-label'>Current Balance</div>
              <div className='balance-value'>{credits !== null ? formatCredits(credits) : '—'}</div>
              <button
                onClick={() => setActiveTab('purchase-credits')}
                className='btn btn-primary btn-sm'
              >
                Purchase Credits
              </button>
            </div>
          </div>

          <div className='sidebar-section'>
            <div className='sidebar-nav'>
              <button
                className={`sidebar-nav-item ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                <span className='nav-icon'>📊</span>Overview
              </button>
              <button
                className={`sidebar-nav-item ${activeTab === 'team' ? 'active' : ''}`}
                onClick={() => setActiveTab('team')}
              >
                <span className='nav-icon'>👥</span>Team
              </button>
              <button
                className={`sidebar-nav-item ${activeTab === 'purchase-credits' ? 'active' : ''}`}
                onClick={() => setActiveTab('purchase-credits')}
              >
                <span className='nav-icon'>💳</span>Balance & Credits
              </button>
              <button
                className={`sidebar-nav-item ${activeTab === 'api-keys' ? 'active' : ''}`}
                onClick={() => setActiveTab('api-keys')}
              >
                <span className='nav-icon'>🔑</span>API Keys
              </button>
            </div>
          </div>

          {groupInfo && (
            <div className='sidebar-section'>
              <div className='group-info'>
                <div className='group-info-label'>Organization</div>
                <div className='group-info-value'>{groupInfo.group_name}</div>
              </div>
            </div>
          )}
        </aside>

        <main className='dashboard-main'>
          {error && <div className='alert alert-error'>{error}</div>}
          {success && <div className='alert alert-success'>{success}</div>}

          {activeTab === 'overview' && (
            <div className='dashboard-section'>
              <h2>Welcome back!</h2>
              <p className='section-description'>
                Manage your credits, invite team members, and generate API keys.
              </p>

              <div className='overview-stats'>
                <div className='overview-stat'>
                  <div className='stat-icon'>💰</div>
                  <div className='stat-content'>
                    <div className='stat-label'>Available Balance</div>
                    <div className='stat-value'>
                      {credits !== null ? formatCredits(credits) : '—'}
                    </div>
                  </div>
                </div>
                <div className='overview-stat'>
                  <div className='stat-icon'>📈</div>
                  <div className='stat-content'>
                    <div className='stat-label'>Spend (Last 30 days)</div>
                    <div className='stat-value'>
                      {spend30dTotal !== null ? `£${spend30dTotal.toFixed(2)}` : '—'}
                    </div>
                  </div>
                </div>
                <div className='overview-stat'>
                  <div className='stat-icon'>🔑</div>
                  <div className='stat-content'>
                    <div className='stat-label'>Active API Keys</div>
                    <div className='stat-value'>{apiKeys.length}</div>
                  </div>
                </div>
              </div>

              <h3 className='dashboard-subheading'>Quick actions</h3>
              <div className='quick-actions'>
                <div className='action-card' onClick={() => setActiveTab('purchase-credits')}>
                  <div className='action-icon'>💳</div>
                  <div className='action-content'>
                    <h3>Manage and View Balance</h3>
                    <p>Check your usage and purchase credits</p>
                  </div>
                </div>
                <div className='action-card' onClick={() => setActiveTab('api-keys')}>
                  <div className='action-icon'>🔑</div>
                  <div className='action-content'>
                    <h3>Generate API Key</h3>
                    <p>Create keys for API integration</p>
                  </div>
                </div>
                <div className='action-card' onClick={() => setActiveTab('team')}>
                  <div className='action-icon'>👥</div>
                  <div className='action-content'>
                    <h3>Invite Team Member</h3>
                    <p>Add users to your organization</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'team' && (
            <div className='dashboard-section'>
              <h2>Team Management</h2>
              <p className='section-description'>
                Invite users to join your organization. They will receive an email with instructions
                to set up their account.
              </p>
              <div className='card'>
                <div className='card-header'>
                  <h3>Invite Team Member</h3>
                </div>
                <div className='card-body'>
                  <form onSubmit={handleAddUser} className='dashboard-form'>
                    <div className='form-group'>
                      <label className='label'>Email Address</label>
                      <input
                        type='email'
                        className='input'
                        value={newUser.username}
                        onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                        placeholder='colleague@example.com'
                        required
                      />
                      <small className='form-hint'>
                        They will be added to {groupInfo?.group_name}
                      </small>
                    </div>
                    <button type='submit' className='btn btn-primary'>
                      Send Invitation
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'purchase-credits' && (
            <div className='dashboard-section'>
              <h2>Balance & Credits</h2>

              {/* Auto Top-up Status */}
              {autoTopupSettings && !isEditingTopup && paymentStep === 'type' && (
                <div className='card auto-topup-status-card'>
                  <div className='card-header'>
                    <h3>🔄 Automatic Top-up Active</h3>
                  </div>
                  <div className='card-body'>
                    <div className='topup-status-grid'>
                      <div className='topup-status-item'>
                        <span className='status-label'>Top-up Amount</span>
                        <span className='status-value'>
                          £{(autoTopupSettings.topup_amount / 100).toFixed(2)}
                        </span>
                      </div>
                      <div className='topup-status-item'>
                        <span className='status-label'>Trigger Threshold</span>
                        <span className='status-value'>
                          £{(autoTopupSettings.threshold / 100).toFixed(2)}
                        </span>
                      </div>
                      <div className='topup-status-item'>
                        <span className='status-label'>Payment Method</span>
                        <span className='status-value'>
                          •••• {autoTopupSettings.last4 || '****'}
                        </span>
                      </div>
                      <div className='topup-status-item'>
                        <span className='status-label'>Status</span>
                        <span className='status-badge status-active'>Active</span>
                      </div>
                    </div>
                    <div className='topup-status-actions'>
                      <button onClick={handleEditTopup} className='btn btn-secondary'>
                        Edit Settings
                      </button>
                      <button
                        onClick={handleDisableAutoTopup}
                        className='btn btn-ghost btn-danger'
                        disabled={isPaymentLoading}
                      >
                        {isPaymentLoading ? (
                          <>
                            <span className='spinner-dark'></span>Disabling...
                          </>
                        ) : (
                          'Disable Auto Top-up'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Edit Auto Top-up Form */}
              {isEditingTopup && (
                <div className='card'>
                  <div className='card-header'>
                    <button onClick={handleCancelEditTopup} className='back-link'>
                      ← Cancel
                    </button>
                    <h3>Edit Auto Top-up Settings</h3>
                  </div>
                  <div className='card-body'>
                    <form onSubmit={handleUpdateAutoTopup} className='payment-form'>
                      <div className='form-group'>
                        <label className='label'>Top-up Amount</label>
                        <div className='input-with-prefix'>
                          <span className='input-prefix'>£</span>
                          <input
                            type='number'
                            className='input input-with-padding'
                            value={editTopupAmount}
                            onChange={(e) => {
                              setEditTopupAmount(e.target.value);
                              setPaymentError('');
                            }}
                            placeholder='50.00'
                            min={MINIMUM_AMOUNT}
                            step='0.01'
                            disabled={isPaymentLoading}
                            required
                          />
                        </div>
                        <small className='form-hint'>Minimum amount: £{MINIMUM_AMOUNT}</small>
                      </div>

                      <div className='form-group'>
                        <label className='label'>Top-up Trigger</label>
                        <div className='input-with-prefix'>
                          <span className='input-prefix'>£</span>
                          <input
                            type='number'
                            className='input input-with-padding'
                            value={editTopupThreshold}
                            onChange={(e) => {
                              setEditTopupThreshold(e.target.value);
                              setPaymentError('');
                            }}
                            placeholder='10.00'
                            min={MINIMUM_THRESHOLD}
                            step='0.01'
                            disabled={isPaymentLoading}
                            required
                          />
                        </div>
                        <small className='form-hint'>
                          Automatically charge when balance falls below this amount (minimum £
                          {MINIMUM_THRESHOLD})
                        </small>
                      </div>

                      {paymentError && <div className='alert alert-error'>{paymentError}</div>}

                      <div className='button-group'>
                        <button
                          type='button'
                          onClick={handleCancelEditTopup}
                          className='btn btn-secondary'
                          disabled={isPaymentLoading}
                        >
                          Cancel
                        </button>
                        <button
                          type='submit'
                          className='btn btn-primary btn-lg'
                          disabled={isPaymentLoading}
                        >
                          {isPaymentLoading ? (
                            <>
                              <span className='spinner'></span>Updating...
                            </>
                          ) : (
                            'Update Settings'
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Payment Type Selection */}
              {paymentStep === 'type' && !isEditingTopup && (
                <>
                  <div className='section-divider' />
                  <p className='section-description'>
                    Choose how you'd like to manage your balance:
                  </p>
                  <div className='payment-type-grid'>
                    <div
                      className='payment-option-card'
                      onClick={() => handleTypeSelection('one-time')}
                    >
                      <div className='payment-option-icon'>💳</div>
                      <div className='payment-option-content'>
                        <h3>One-time Purchase</h3>
                        <p>Add balance now with a single payment</p>
                      </div>
                    </div>
                    <div
                      className='payment-option-card'
                      onClick={() => handleTypeSelection('auto-topup')}
                    >
                      <div className='payment-option-icon'>🔄</div>
                      <div className='payment-option-content'>
                        <h3>Automatic Top-up</h3>
                        <p>
                          {autoTopupSettings
                            ? 'Replace existing auto top-up settings'
                            : 'Set up automatic payments when your balance runs low'}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Amount Screen */}
              {paymentStep === 'amount' && !isEditingTopup && (
                <div className='card'>
                  <div className='card-header'>
                    <button onClick={resetPaymentFlow} className='back-link'>
                      ← Back to Options
                    </button>
                    <h3>
                      {paymentType === 'auto-topup'
                        ? 'Set Up Automatic Top-up'
                        : 'One-time Purchase'}
                    </h3>
                  </div>
                  <div className='card-body'>
                    <form onSubmit={handleAmountSubmit} className='payment-form'>
                      <div className='form-group'>
                        <label className='label'>
                          {paymentType === 'auto-topup' ? 'Top-up Amount' : 'Amount'}
                        </label>
                        <div className='input-with-prefix'>
                          <span className='input-prefix'>£</span>
                          <input
                            type='number'
                            className='input input-with-padding'
                            value={amount}
                            onChange={(e) => {
                              setAmount(e.target.value);
                              setPaymentError('');
                            }}
                            placeholder='50.00'
                            min={MINIMUM_AMOUNT}
                            step='0.01'
                            disabled={isPaymentLoading}
                            required
                          />
                        </div>
                        <small className='form-hint'>Minimum amount: £{MINIMUM_AMOUNT}</small>
                      </div>

                      {paymentType === 'auto-topup' && (
                        <>
                          <div className='form-group'>
                            <label className='label'>Top-up Trigger</label>
                            <div className='input-with-prefix'>
                              <span className='input-prefix'>£</span>
                              <input
                                type='number'
                                className='input input-with-padding'
                                value={threshold}
                                onChange={(e) => {
                                  setThreshold(e.target.value);
                                  setPaymentError('');
                                }}
                                placeholder='10.00'
                                min={MINIMUM_THRESHOLD}
                                step='0.01'
                                disabled={isPaymentLoading}
                                required
                              />
                            </div>
                            <small className='form-hint'>
                              Automatically charge when balance falls below this amount (minimum £
                              {MINIMUM_THRESHOLD})
                            </small>
                          </div>
                        </>
                      )}

                      <div className='form-group'>
                        <label className='label'>Billing Email</label>
                        <input
                          type='email'
                          className='input'
                          value={billingEmail}
                          onChange={(e) => {
                            setBillingEmail(e.target.value);
                            setPaymentError('');
                          }}
                          placeholder='billing@example.com'
                          disabled={isPaymentLoading}
                          required
                        />
                        <small className='form-hint'>
                          {paymentType === 'auto-topup' ? (
                            <>VAT receipts for automatic top-ups will be sent here</>
                          ) : (
                            <>VAT receipts for one-time purchases will be sent here</>
                          )}
                        </small>
                      </div>

                      {paymentError && <div className='alert alert-error'>{paymentError}</div>}

                      <button
                        type='submit'
                        className='btn btn-primary btn-lg'
                        disabled={isPaymentLoading || !amount || !billingEmail.trim()}
                      >
                        {isPaymentLoading ? (
                          <>
                            <span className='spinner'></span>Loading...
                          </>
                        ) : (
                          'Continue to Payment'
                        )}
                      </button>
                    </form>
                  </div>
                </div>
              )}

              {/* One-time Payment */}
              {paymentStep === 'payment' && clientSecret && !isEditingTopup && (
                <div className='card'>
                  <div className='card-header'>
                    <button onClick={handleBackToAmount} className='back-link'>
                      ← Change Amount
                    </button>
                    <h3>Complete Payment</h3>
                  </div>
                  <div className='card-body'>
                    {!stripePromise ? (
                      <div className='alert alert-error'>
                        Error: Issue with payment system. Please try again later.
                      </div>
                    ) : (
                      <Elements
                        stripe={stripePromise}
                        options={{
                          clientSecret,
                          appearance: {
                            theme: 'stripe',
                            variables: { colorPrimary: '#0066cc' },
                          },
                        }}
                      >
                        <CheckoutForm
                          amount={totalWithTax || parseFloat(amount).toFixed(2)}
                          onSuccess={() => {
                            setSuccess('Payment successful! Your credits will be added shortly.');
                            loadData();
                            resetPaymentFlow();
                          }}
                          onError={(e) => setPaymentError(e.message)}
                        />
                      </Elements>
                    )}
                  </div>
                </div>
              )}

              {/* Auto Top-up Setup */}
              {paymentStep === 'setup' && setupSecret && !isEditingTopup && (
                <div className='card'>
                  <div className='card-header'>
                    <button onClick={handleBackToAmount} className='back-link'>
                      ← Change Settings
                    </button>
                    <h3>Set Up Automatic Top-up</h3>
                  </div>
                  <div className='card-body'>
                    {!stripePromise ? (
                      <div className='alert alert-error'>
                        Error: Stripe not initialized. Check your configuration.
                      </div>
                    ) : (
                      <Elements
                        stripe={stripePromise}
                        options={{
                          clientSecret: setupSecret,
                          appearance: {
                            theme: 'stripe',
                            variables: { colorPrimary: '#0066cc' },
                          },
                        }}
                      >
                        <SetupForm
                          amount={parseFloat(amount).toFixed(2)}
                          threshold={parseFloat(threshold).toFixed(2)}
                          billingEmail={billingEmail}
                          onSuccess={() => {
                            setSuccess('Auto top-up enabled successfully!');
                            loadAutoTopupSettings();
                            resetPaymentFlow();
                          }}
                          onError={(e) => setPaymentError(e.message)}
                          onBack={handleBackToAmount}
                        />
                      </Elements>
                    )}
                  </div>
                </div>
              )}

              {/* Spend Overview */}
              {paymentStep === 'type' && !isEditingTopup && (
                <>
                  <div className='section-divider' />
                  <div className='overview-header'>
                    <p className='section-description'>Overview of your spend and usage data</p>
                    <select
                      className='overview-period-select'
                      value={spendPeriod}
                      onChange={(e) => setSpendPeriod(e.target.value)}
                      disabled={spendLoading}
                    >
                      {SPEND_PERIOD_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className='dash-table-container'>
                    <table className='dash-table'>
                      <thead>
                        <tr>
                          <th>Service</th>
                          <th>Transactions</th>
                          <th>Credits Used</th>
                        </tr>
                      </thead>
                      <tbody>
                        {spendLoading && !currentSpendRows ? (
                          <tr>
                            <td colSpan={3}>Loading...</td>
                          </tr>
                        ) : spendError && !currentSpendRows ? (
                          <tr>
                            <td colSpan={3}>{spendError}</td>
                          </tr>
                        ) : spendRows.length === 0 ? (
                          <tr>
                            <td colSpan={3}>No usage recorded for this period.</td>
                          </tr>
                        ) : (
                          spendRows.map((row) => (
                            <tr key={row.service}>
                              <td>{row.service}</td>
                              <td>{row.transaction_count}</td>
                              <td>£{row.credits_consumed.toFixed(2)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td>Total</td>
                          <td>{spendTotalTransactions}</td>
                          <td>£{spendTotalCredits.toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'api-keys' && (
            <div className='dashboard-section'>
              <h2>API Keys</h2>
              <p className='section-description'>
                API keys allow you to authenticate requests to the DocXS API. Keep your keys secure
                and never share them publicly.
              </p>

              {generatedKey && (
                <div className='card key-reveal-card'>
                  <div className='card-header'>
                    <h3>Your New API Key</h3>
                  </div>
                  <div className='card-body'>
                    <div className='alert alert-info'>
                      <strong>Important:</strong> Save this key now. You won't be able to see it
                      again.
                    </div>
                    <div className='key-display'>
                      <code>{generatedKey.api_key}</code>
                      <button
                        className='btn btn-secondary btn-sm'
                        onClick={() => {
                          navigator.clipboard.writeText(generatedKey.api_key);
                          setSuccess('Copied to clipboard!');
                        }}
                      >
                        Copy
                      </button>
                    </div>
                    <button className='btn btn-ghost btn-sm' onClick={() => setGeneratedKey(null)}>
                      Dismiss
                    </button>
                  </div>
                </div>
              )}

              <div className='card'>
                <div className='card-header'>
                  <h3>Generate New API Key</h3>
                </div>
                <div className='card-body'>
                  <form onSubmit={handleGenerateApiKey} className='dashboard-form'>
                    <div className='form-group'>
                      <label className='label'>Key Name</label>
                      <input
                        type='text'
                        className='input'
                        value={newApiKeyName}
                        onChange={(e) => setNewApiKeyName(e.target.value)}
                        placeholder='Production Server'
                        maxLength={100}
                        required
                      />
                      <small className='form-hint'>
                        A descriptive name to help you identify this key
                      </small>
                    </div>
                    <button type='submit' className='btn btn-primary'>
                      Generate API Key
                    </button>
                  </form>
                </div>
              </div>

              <div className='section-divider'></div>

              <h3>Active API Keys</h3>
              {apiKeys.length === 0 ? (
                <div className='empty-state'>
                  <div className='empty-icon'>🔑</div>
                  <p>No API keys yet</p>
                  <p className='empty-hint'>Generate your first API key to get started</p>
                </div>
              ) : (
                <div className='keys-list'>
                  {apiKeys.map((key, idx) => (
                    <div key={idx} className='key-item'>
                      <div className='key-info'>
                        <div className='key-name'>{key.name}</div>
                        <div className='key-meta'>
                          <span>Created: {formatDate(key.created_at)}</span>
                        </div>
                      </div>
                      <button
                        className='btn btn-ghost btn-sm btn-danger'
                        onClick={() => handleRevokeApiKey(key.name)}
                      >
                        Revoke
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className='section-divider'></div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default UserDashboard;
